/**
 * Возврат с Т-Банка: сколько ждать ответа банка, какие слова показывать под
 * конкретный товар и как запомнить «платёж ещё обрабатывается», чтобы окно
 * покупки не предлагало заплатить второй раз.
 *
 * Чистые функции без React — их проверяет paymentProcessing.test.ts.
 */

/**
 * Сколько опрашиваем статус, прежде чем показать итог «платёж ещё обрабатывается».
 * Раньше опрос обрывался через 12×2,5 с ≈ 30 с: при медленном банке человек
 * уходил к тарифам и платил повторно (аудит премиума 19.10, §3 п. 6).
 */
export const PAYMENT_POLL_MAX_MS = 5 * 60_000;

/**
 * Пауза перед следующим опросом; null — время вышло, опрос прекращаем.
 * Первые 30 с — часто (обычный платёж подтверждается за секунды), потом реже,
 * чтобы пять минут ожидания не превращались в сотню запросов.
 */
export function nextPaymentPollDelay(elapsedMs: number): number | null {
    if (elapsedMs >= PAYMENT_POLL_MAX_MS) return null;
    if (elapsedMs < 30_000) return 2_500;
    if (elapsedMs < 90_000) return 5_000;
    return 10_000;
}

/**
 * Что сказал сервер на опрос статуса платежа. Раньше любая ошибка getPayment
 * (404 чужого платежа, 5xx, обрыв сети) читалась как pending — и страница
 * возврата 5 минут держала неубираемое окно «не выходите» (#1238).
 */
export type PaymentPollOutcome =
    | {kind: "status"; status: string}
    /** 400/403/404: платежа нет (или не наш) — ждать нечего. */
    | {kind: "not_found"}
    /** 5xx/401/сеть/таймаут — можно повторить. */
    | {kind: "transient"};

/** Столько временных сбоев подряд — и опрос останавливается с сообщением. */
export const PAYMENT_CHECK_MAX_FAILURES = 3;

export function classifyPaymentPoll(r: {
    data: {status?: string | null} | null;
    status?: number;
}): PaymentPollOutcome {
    const status = r.data?.status;
    if (typeof status === "string" && status) return {kind: "status", status};
    if (r.status === 400 || r.status === 403 || r.status === 404) return {kind: "not_found"};
    return {kind: "transient"};
}

/**
 * Окно «платёж обрабатывается» (закрыть нельзя) — только пока идёт опрос и
 * сервер сам ответил pending. Ошибка запроса окно не открывает.
 */
export function showPaymentProcessingWindow(s: {
    polling: boolean;
    serverStatus: string | null;
    failed: boolean;
    isMockPending: boolean;
}): boolean {
    return s.polling && s.serverStatus === "pending" && !s.failed && !s.isMockPending;
}

const PRODUCT_NAMES: Record<string, string> = {
    subscription: "Премиум подписка",
    analysis: "Подробный разбор совместимости",
    analysis_unlimited: "Пакет разборов совместимости",
    analysis_refresh: "Обновление разбора",
    superlike_pack: "Пакет супер симпатий",
    gift: "Подарок",
};

/** Подписи тарифов — зеркало SUBSCRIPTION_PLANS в SubscriptionModal. */
const PLAN_LABELS: Record<string, string> = {
    "1m": "1 месяц",
    "3m": "3 месяца",
    "6m": "6 месяцев",
};

/** Название товара для чека: «Премиум подписка · 3 месяца». */
export function paymentProductName(kind: string | undefined, metadata?: Record<string, unknown> | null): string {
    if (!kind) return "Оплата";
    const base = PRODUCT_NAMES[kind] ?? "Оплата";
    if (kind === "subscription") {
        const plan = typeof metadata?.plan === "string" ? PLAN_LABELS[metadata.plan] : undefined;
        return plan ? `${base} · ${plan}` : base;
    }
    return base;
}

export function isAnalysisKind(kind: string | undefined): boolean {
    return kind === "analysis" || kind === "analysis_unlimited" || kind === "analysis_refresh";
}

export interface PaymentKindCopy {
    /** Подпись кнопки возврата, пока платёж не завершён. */
    back: string;
    /** Что произойдёт после подтверждения банком — для экрана «ещё обрабатывается». */
    activation: string;
    /** Подзаголовок успеха. */
    paid: string;
}

/**
 * Тексты страницы возврата под конкретный товар. Раньше страница для любой
 * покупки говорила про разбор («Вернуться к анкете», «Разбор откроется…») —
 * в том числе после оплаты подписки.
 */
export function paymentKindCopy(kind: string | undefined): PaymentKindCopy {
    if (isAnalysisKind(kind)) {
        return {
            back: "Вернуться к анкете",
            activation: "Разбор откроется, как только банк подтвердит платёж.",
            paid: "Средства зачислены. Возвращаем вас к разбору.",
        };
    }
    if (kind === "subscription") {
        return {
            back: "Вернуться в приложение",
            activation: "Подписка включится сама, как только банк подтвердит платёж. Повторно оплачивать не нужно.",
            paid: "Подписка активна. Возвращаем вас в приложение.",
        };
    }
    if (kind === "superlike_pack") {
        return {
            back: "Вернуться в приложение",
            activation: "Супер симпатии начислятся сами, как только банк подтвердит платёж. Повторно оплачивать не нужно.",
            paid: "Супер симпатии начислены. Возвращаем вас в приложение.",
        };
    }
    return {
        back: "Вернуться в приложение",
        activation: "Покупка активируется сама, как только банк подтвердит платёж. Повторно оплачивать не нужно.",
        paid: "Средства зачислены. Возвращаем вас в приложение.",
    };
}

/* ── Запомненный незавершённый платёж ─────────────────────────────────── */

/**
 * Платёж, по которому человек вернулся с банка, а подтверждения ещё нет.
 * Окно тарифов читает запись и, если сервер всё ещё отвечает pending,
 * предупреждает: «предыдущий платёж обрабатывается». Серверная история
 * (/payments/history) pending-платежи не отдаёт, поэтому запись — клиентская.
 */
export interface PendingPaymentRecord {
    paymentId: string;
    kind: string;
    savedAt: number;
}

const PENDING_KEY = "rl.payment-pending";
/** Старше суток — не показываем: банк к этому времени платёж либо провёл, либо отменил. */
export const PENDING_PAYMENT_TTL_MS = 24 * 60 * 60 * 1000;

function storage(): Storage | null {
    try {
        return typeof localStorage === "undefined" ? null : localStorage;
    } catch {
        return null;
    }
}

export function rememberPendingPayment(paymentId: string, kind: string, now = Date.now()): void {
    try {
        storage()?.setItem(PENDING_KEY, JSON.stringify({paymentId, kind, savedAt: now}));
    } catch {
        // Приватный режим/переполнение — подсказки просто не будет.
    }
}

/** Убрать запись; с paymentId — только если она про этот платёж. */
export function forgetPendingPayment(paymentId?: string): void {
    try {
        const s = storage();
        if (!s) return;
        if (paymentId) {
            const current = readPendingPayment();
            if (current && current.paymentId !== paymentId) return;
        }
        s.removeItem(PENDING_KEY);
    } catch {
        // Недоступный storage — значит, и записи там нет.
    }
}

export function readPendingPayment(kind?: string, now = Date.now()): PendingPaymentRecord | null {
    try {
        const raw = storage()?.getItem(PENDING_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PendingPaymentRecord;
        if (typeof parsed?.paymentId !== "string" || typeof parsed.kind !== "string") return null;
        if (now - (parsed.savedAt ?? 0) > PENDING_PAYMENT_TTL_MS) return null;
        if (kind && parsed.kind !== kind) return null;
        return parsed;
    } catch {
        return null;
    }
}
